import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { getRecord } from '@utils/apiUtils';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import Sidebar from '@/shared/layout/Sidebar';
import OnboardingGuide from '@/shared/layout/OnboardingGuide';
import { ENTITY_NAME_FIRM } from '@/shared/adapters/constants';
import { useIdleRecovery } from '@/shared/hooks/useIdleRecovery';
import { clearSessionProfileImage, hydrateSessionProfileImage } from '@/shared/utils/sessionProfileImage';
import { estimatePctFromCompletedAgents } from '@/features/onboarding/utils/crawlProgress';
import type { OnboardingResult } from '@/features/onboarding/types';
import { useOnboardingResume } from '@/features/onboarding/hooks/useOnboardingResume';
import { useRescanController } from '@/features/rescan/RescanController';
import { useActiveBuildRun, type FinishedBuildRun } from '@/features/onboarding/hooks/useActiveBuildRun';
import type { CustomerPersonaCard } from '@/features/onboarding/components/CustomersPanel';
import OnboardingProgressBar from '@/shared/layout/OnboardingProgressBar';
import OnboardingAlertBar from '@/shared/layout/OnboardingAlertBar';
import { buildProgressFromSteps } from '@/features/onboarding/utils/buildProgress';
import { APP_ROUTES } from '@/app/routePaths';
import { isUpgradeInProgress, subscribeUpgradeInProgress } from '@/shared/utils/upgradeInProgress';
import { isCheckoutInProgress, getCheckoutMessage, subscribeCheckoutInProgress } from '@/shared/utils/checkoutInProgress';
import { showSuccessMessage, showErrorMessage } from '@/shared/adapters/toast';
import { _t, _tParts } from '@/i18n/t';
import '@/styles/design-system.css';

const Subscription = lazy(() => import('@/features/settings/pages/Subscription'));
const CrawlStatus = lazy(() => import('@/features/onboarding/pages/CrawlStatus'));

interface AuthUser {
  id?: number;
  firmId?: number;
  profilePicture?: string | null;
  subscription?: {
    planName: string;
    status: string;
    hasUnpaidInvoice?: boolean;
  };
  paymentCustomerId?: string;
  isPaymentEnabled?: boolean;
}

interface AuthState {
  auth: {
    user?: AuthUser;
  };
}

const Layout = () => {
  useIdleRecovery();
  const auth = useSelector((state: AuthState) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const firmId = auth?.user?.firmId;
  const userId = auth?.user?.id;
  const profilePicture = auth?.user?.profilePicture;
  const [showOnboarding, setShowOnboarding] = useState(false);

  const [showAlertBar, setShowAlertBar] = useState(false);
  const [analysisProfile, setAnalysisProfile] = useState<OnboardingResult | null>(null);
  const [crawlContext, setCrawlContext] = useState<{
    name: string;
    url: string;
    scanId: string | null;
    pendingResult: OnboardingResult | null;
    minimized: boolean;
    pct: number;
    completedAgents?: string[];
    agentMessages?: Record<string, string[]>;
    personas?: CustomerPersonaCard[];
  } | null>(null);

  const [finishedBuild, setFinishedBuild] = useState<FinishedBuildRun | null>(null);
  const { data: activeBuildRun = null } = useActiveBuildRun(!!firmId, run => setFinishedBuild(run));

  const [upgradeInProgress, setUpgradeInProgressState] = useState(isUpgradeInProgress());
  useEffect(() => {
    return subscribeUpgradeInProgress(() => {
      setUpgradeInProgressState(isUpgradeInProgress());
    });
  }, []);

  const [checkoutInProgress, setCheckoutInProgressState] = useState(isCheckoutInProgress());
  const [checkoutMessage, setCheckoutMessage] = useState(getCheckoutMessage());
  useEffect(() => {
    return subscribeCheckoutInProgress(() => {
      setCheckoutInProgressState(isCheckoutInProgress());
      setCheckoutMessage(getCheckoutMessage());
    });
  }, []);

  useEffect(() => {
    const fetchFirmPreference = async () => {
      if (!firmId) return;
      try {
        const firm = await getRecord(ENTITY_NAME_FIRM, firmId);
        setShowOnboarding(firm?.showOnboarding !== false);
      } catch {
        setShowOnboarding(false);
      }
    };
    void fetchFirmPreference();
  }, [firmId]);

  useEffect(() => {
    if (!userId || !profilePicture) {
      clearSessionProfileImage();
      return;
    }
    void hydrateSessionProfileImage(userId, profilePicture).catch(() => {});
  }, [profilePicture, userId]);

  useEffect(() => {
    const bg = (location.state as Record<string, unknown> | null)?.backgroundCrawl as {
      name: string;
      url: string;
      scanId: string | null;
      pct: number;
      pendingResult: OnboardingResult;
    } | undefined;
    if (bg) {
      setCrawlContext({ ...bg, minimized: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Falls back to the resumable-scan endpoint so the "Analyzing" chip survives a hard
  // reload while backgrounded, since router state (above) doesn't survive that.
  const resume = useOnboardingResume();
  const { adoptRunning } = useRescanController();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (resume.status !== 'scan') return;
    if (resume.rescan) {
      if (resume.companyId === null) return;
      adoptRunning({
        companyId: resume.companyId,
        scanId: resume.scanId,
        rescanDbId: resume.scanDbId,
        websiteUrl: resume.intake.url,
        companyName: resume.intake.name,
        hasPrevious: resume.hasPrevious,
        completedAgents: resume.completedAgents,
      });
      return;
    }
    setCrawlContext(prev => prev ?? {
      name: resume.intake.name,
      url: resume.intake.url,
      scanId: resume.scanId,
      pendingResult: null,
      minimized: true,
      pct: estimatePctFromCompletedAgents(resume.completedAgents),
      completedAgents: resume.completedAgents,
      agentMessages: resume.agentMessages,
      personas: resume.personas,
    });
  }, [resume, adoptRunning]);

  const handleCrawlDone = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['media'] });
    setCrawlContext(prev => {
      if (prev) {
        if (prev.pendingResult) setAnalysisProfile(prev.pendingResult);
        setShowAlertBar(true);
        showSuccessMessage(_t('onboarding.crawl.doneToast'));
      }
      return null;
    });
  }, [queryClient]);

  const handleCrawlError = useCallback(() => {
    setCrawlContext(null);
    showErrorMessage(_t('onboarding.crawl.errorToast'));
  }, []);

  const subscription = auth.user?.subscription;
  const paymentCustomerId = auth.user?.paymentCustomerId;
  const loggedInUser = auth.user;
  const isPaymentEnabled = auth.user?.isPaymentEnabled;

  let isSubscriptionValid = true;
  if (isPaymentEnabled && loggedInUser && paymentCustomerId) {
    if (!subscription) {
      isSubscriptionValid = false;
    } else {
      const { planName, status, hasUnpaidInvoice } = subscription;
      
      if (planName === 'Evaluation') {
        if (status !== 'trialing') {
          isSubscriptionValid = false;
        }
      } else {
        if (status === 'active' || status === 'trialing') {
          isSubscriptionValid = true;
        } else if (['past_due', 'incomplete'].includes(status) && hasUnpaidInvoice === false) {
          isSubscriptionValid = true;
        } else {
          isSubscriptionValid = false;
        }
      }
    }
  }

  const showSubscriptionOverlay = !isSubscriptionValid && !upgradeInProgress;

  const showCrawlBar = !!crawlContext && !location.pathname.includes('/onboarding');
  const showBuildBar = !!activeBuildRun && !location.pathname.includes('/analysis');
  const progressItems = [
    ...(showCrawlBar ? [{
      key: 'crawl',
      label: _t('onboarding.crawl.eyebrowAnalyzing'),
      pct: crawlContext!.pct,
      onView: () => navigate(APP_ROUTES.ONBOARDING, { state: { resumeCrawl: crawlContext } }),
      viewLabel: _t('onboarding.build.viewProgress'),
    }] : []),
    ...(showBuildBar ? [{
      key: 'build',
      label: _t('onboarding.build.backgroundChip'),
      pct: buildProgressFromSteps(activeBuildRun!.steps ?? []).pct,
      onView: () => navigate(APP_ROUTES.ANALYSIS),
      viewLabel: _t('onboarding.build.viewProgress'),
    }] : []),
  ];

  return (
    <div className="th w-screen h-screen overflow-hidden bg-background flex flex-col">
      <OnboardingProgressBar items={progressItems} />
      {crawlContext && !location.pathname.includes('/onboarding') && (
        /* Rendered minimized (invisible) purely to keep the SSE connection alive so
           completion and progress are tracked while the user is away from /onboarding. */
        <Suspense fallback={null}>
          <CrawlStatus
            name={crawlContext.name}
            url={crawlContext.url}
            scanId={crawlContext.scanId}
            minimized
            initialPct={crawlContext.pct}
            initialCompletedAgents={crawlContext.completedAgents}
            initialAgentMessages={crawlContext.agentMessages}
            initialPersonas={crawlContext.personas}
            onPctChange={(p: number) => setCrawlContext(prev => prev ? { ...prev, pct: p } : null)}
            onDone={handleCrawlDone}
            onError={handleCrawlError}
          />
        </Suspense>
      )}
      {checkoutInProgress && (
        <div
          className="fixed inset-0 z-[998] flex flex-col items-center justify-center gap-3 bg-black/50 backdrop-blur-sm"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <Loader2 className="w-8 h-8 animate-spin text-primary-foreground" />
          <span className="text-sm font-medium text-primary-foreground max-w-sm text-center px-4">
            {checkoutMessage || _t('settings.subscription.completingRequest')}
          </span>
        </div>
      )}
      {showSubscriptionOverlay && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center overflow-auto bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-5xl min-w-fit mx-auto my-auto px-4 py-8">
            <div className="bg-white rounded-xl shadow-lg">
              <Suspense fallback={null}>
                <Subscription isOverlay={true} />
              </Suspense>
            </div>
          </div>
        </div>
      )}
      <div className="flex-1 min-h-0 w-screen flex overflow-hidden">
        {!location.pathname.includes('/onboarding') && !location.pathname.includes('/analysis') && <Sidebar />}
        <main className="flex-1 flex flex-col overflow-hidden pl-0 min-w-0">
          <div className="flex-1 overflow-hidden rounded-xl bg-workspace border-0 border-l-2 border-l-panel-separator [border-left-style:solid] flex flex-col">
            {showAlertBar && (
              <OnboardingAlertBar
                actionLabel={_t('onboarding.analysisBar.action')}
                onAction={() => { navigate(APP_ROUTES.ANALYSIS, { state: analysisProfile }); setShowAlertBar(false); }}
                onDismiss={() => setShowAlertBar(false)}
                dismissLabel={_t('onboarding.analysisBar.dismiss')}
              >
                {_tParts(
                  analysisProfile?.name ? 'onboarding.analysisBar.messageNamed' : 'onboarding.analysisBar.message',
                  { name: analysisProfile?.name ?? '', cta: _t('onboarding.analysisBar.cta') },
                ).map((part, index) => (
                  typeof part === 'string'
                    ? <span key={index}>{part}</span>
                    : part.key === 'cta'
                      ? <strong key={index} className="font-semibold">{part.value}</strong>
                      : <span key={index}>{part.value}</span>
                ))}
              </OnboardingAlertBar>
            )}
            {finishedBuild?.contentId && (
              <OnboardingAlertBar
                actionLabel={_t('onboarding.build.alertBar.action')}
                onAction={() => { navigate(APP_ROUTES.contentOptimize(finishedBuild.contentId!)); setFinishedBuild(null); }}
                onDismiss={() => setFinishedBuild(null)}
                dismissLabel={_t('onboarding.build.alertBar.dismiss')}
              >
                {_t('onboarding.build.alertBar.message')}
              </OnboardingAlertBar>
            )}
            <div className="flex flex-1 min-h-0 overflow-hidden">
              <div className="min-w-0 flex-1 overflow-auto">
                <Outlet />
              </div>
              {showOnboarding && !location.pathname.includes('/analysis') && <OnboardingGuide onHide={() => setShowOnboarding(false)} />}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
